import { IReduxState } from 'app/store';
import { IconArrow } from 'common/components/Icons/IconArrow/IconArrow';
import { replaceAllRows } from 'features/GridSlice';
import { showSuccess, showError } from 'features/AlertSlice';
import { RestApi } from 'common/services/rest-api.service';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { LoginService } from 'login/services/login.service';
import { PopUp } from 'common/components/popUp/PopUp';
import { AddAssetWidget } from './AddAssetWidget/AddAssetWidget';
import { BulkRemoveAssetWidget } from './BulkRemoveAssetWidget/BulkRemoveAssetWidget';
import { KeyDomainsDeletePopup } from './KeyDomainsDeletePopup/KeyDomainsDeletePopup';
import { extractErrorMessage } from 'common/utils/errorHandler';
import './AssetManagementActions.scss';

interface AssetManagementActionsProps {
  ids: string[];
  tab: string;
  subTab: string;
  identifier: string;
}

export function AssetManagementActions(props: AssetManagementActionsProps) {
  const dispatch = useDispatch<any>();
  const ref = useRef<HTMLDivElement>(null);
  const [actionsMenuOpen, setActionsMenuOpen] = useState<boolean>(false);
  const [showAddAsset, setShowAddAsset] = useState<boolean>(false);
  const [showBulkRemove, setShowBulkRemove] = useState<boolean>(false);
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const [deleteAction, setDeleteAction] = useState<'delete-and-blacklist' | 'delete-and-retain' | 'delete-related-names'>('delete-and-blacklist');
  const { user } = useSelector((state: IReduxState) => state.login);
  const gridState = useSelector((state: IReduxState) => state.grid);

  const handleClickOutside = (event: Event) => {
    if (ref.current && !ref.current.contains(event.target as Node)) {
      setActionsMenuOpen(false);
      document.removeEventListener('click', handleClickOutside, true);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  const isAnyRowUpdating = () => {
    return gridState.rows.some((row: any) => row.isUpdating === true);
  };

  const onDeleteAndBlacklist = () => {
    if (isButtonsDisabled) return;
    setDeleteAction('delete-and-blacklist');
    setShowDeletePopup(true);
    setActionsMenuOpen(false);
  };

  const onDeleteAndRetain = () => {
    if (isButtonsDisabled) return;
    setDeleteAction('delete-and-retain');
    setShowDeletePopup(true);
    setActionsMenuOpen(false);
  };

  const onDeleteRelatedNames = () => {
    if (isButtonsDisabled) return;
    setDeleteAction('delete-related-names');
    setShowDeletePopup(true);
    setActionsMenuOpen(false);
  };

  const handlePopupClose = () => {
    setShowDeletePopup(false);
  };

  const handleAddAsset = (value: string, onComplete: () => void) => {
    const lines = value.split('\n').filter(line => line.trim().length > 0);
    const assets = lines.map(line => line.trim());

    const payload = {
      assets: assets
    };

    let apiEndpoint = '';
    if (props.subTab === 'key-domains') {
      apiEndpoint = 'settings/asset-management/seed-data/key-domains/';
    } else if (props.subTab === 'related-names') {
      apiEndpoint = 'settings/asset-management/seed-data/related-names/';
    }

    RestApi.setData(apiEndpoint, payload, 'POST').subscribe(
      (response: any) => {
        dispatch(showSuccess(`Successfully added ${assets.length} asset${assets.length > 1 ? 's' : ''}`));

        // Update grid with the response results
        if (response && response.results !== undefined) {
          dispatch(replaceAllRows({ rows: response.results, count: response.count }));
        }

        onComplete();
      },
      (error: any) => {
        const errorMessage = extractErrorMessage(error, 'Failed to add assets. Please try again.');
        dispatch(showError(errorMessage));
        onComplete();
      }
    );
  };

  const handleBulkRemove = (value: string, deleteAndBlacklist: boolean, onComplete: () => void) => {
    const lines = value.split('\n').filter(line => line.trim().length > 0);
    const assets = lines.map(line => line.trim());

    const payload = {
      assets: assets
    };

    let apiEndpoint = '';
    if (props.subTab === 'key-domains') {
      if (deleteAndBlacklist) {
        apiEndpoint = 'settings/asset-management/seed-data/key-domains/delete/';
      } else {
        apiEndpoint = 'settings/asset-management/seed-data/key-domains/remove/';
      }
    } else if (props.subTab === 'related-names') {
      // For related names, we use the same endpoint regardless of the toggle
      apiEndpoint = 'settings/asset-management/seed-data/related-names/delete/';
    }

    RestApi.setData(apiEndpoint, payload, 'POST').subscribe(
      (response: any) => {
        const actionText = props.subTab === 'key-domains'
          ? (deleteAndBlacklist ? 'deleted and blacklisted' : 'deleted and retained')
          : 'removed';

        dispatch(showSuccess(`Successfully ${actionText} ${assets.length} asset${assets.length > 1 ? 's' : ''}`));

        // Update grid with the response results
        if (response && response.results !== undefined) {
          dispatch(replaceAllRows({ rows: response.results, count: response.count }));
        }

        onComplete();
      },
      (error: any) => {
        const errorMessage = extractErrorMessage(error, 'Failed to remove assets. Please try again.');
        dispatch(showError(errorMessage));
        onComplete();
      }
    );
  };

  if (LoginService.isMemberOrBelow(user)) {
    return <div></div>;
  }

  if (props.tab !== 'seed-data') {
    return <div></div>;
  }

  const isButtonsDisabled = !props.ids.length || isAnyRowUpdating();

  return (
    <div className="AssetManagementActions" ref={ref}>
      <button className="btn btn-main" onClick={() => setShowAddAsset(true)}>
        Add Asset
      </button>
      <button className="btn btn-secondary" onClick={() => setShowBulkRemove(true)}>
        Bulk Remove
      </button>
      <span className={`actions-trigger ${actionsMenuOpen ? 'open' : ''}`}>
        <div className="trigger-wrapper" onClick={() => setActionsMenuOpen(!actionsMenuOpen)}>
          Actions
          <IconArrow />
        </div>
        <ul>
          {props.subTab === 'key-domains' ? (
            <>
              <li className={isButtonsDisabled ? 'disabled' : ''} onClick={onDeleteAndBlacklist}>
                Delete and Blacklist
              </li>
              <li className={isButtonsDisabled ? 'disabled' : ''} onClick={onDeleteAndRetain}>
                Delete and Retain
              </li>
            </>
          ) : (
            <li className={isButtonsDisabled ? 'disabled' : ''} onClick={onDeleteRelatedNames}>
              Delete
            </li>
          )}
        </ul>
      </span>
      <AddAssetWidget
        visible={showAddAsset}
        onClose={() => setShowAddAsset(false)}
        onSubmit={handleAddAsset}
        subTab={props.subTab}
      />

      <BulkRemoveAssetWidget
        visible={showBulkRemove}
        onClose={() => setShowBulkRemove(false)}
        onSubmit={handleBulkRemove}
        subTab={props.subTab}
      />

      {(props.subTab === 'key-domains' || props.subTab === 'related-names') && (
        <PopUp visible={showDeletePopup} setVisible={handlePopupClose}>
          <KeyDomainsDeletePopup
            onClose={handlePopupClose}
            assets={props.ids}
            action={deleteAction}
          />
        </PopUp>
      )}
    </div>
  );
}
